import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import axiosOrg, { AxiosError, AxiosResponse } from "axios";
import toast from "react-hot-toast";
import styled from "styled-components";

//MUI components
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";

//custom components
import PdfViewer from "./PdfViewer.component";

//hooks
import { usePageError } from "../../../hooks/hooks";

//icons
import { IconClose } from "../../../Icons/icons";

//utils
import axios, { baseURL } from "../../../utils/api";

export type MediaFile = {
  category_id: number;
  description: string;
  id: number;
  name: string;
  order: number;
  path: {
    extension: string;
    path: string;
  };
  published_at: string;
  type: string;
};

interface Props {
  file: MediaFile | null;
  closeFn: () => void;
  groupTitle: string;
}

const HtmlInnerContent = styled.div`
  input {
    display: none;
  }
  .tabs {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 4px -2px rgba(0, 0, 0, 0.5);
  }

  .tab {
    width: 100%;
    color: white;
    overflow: hidden;
  }
  .tab-label {
    display: flex;
    justify-content: space-between;
    padding: 1em;
    background: #2c3e50;
    font-weight: bold;
    cursor: pointer;
    /* Icon */
  }
  .tab-label:hover {
    background: #1a252f;
  }
  .tab-label::after {
    content: "❯";
    width: 1em;
    height: 1em;
    text-align: center;
    transition: all 0.35s;
  }
  .tab-content {
    max-height: 0;
    padding: 0 1em;
    color: #2c3e50;
    background: white;
    transition: all 0.35s;
  }
  .tab-close {
    display: flex;
    justify-content: flex-end;
    padding: 1em;
    font-size: 0.75em;
    background: #2c3e50;
    cursor: pointer;
  }
  .tab-close:hover {
    background: #1a252f;
  }

  input:checked + .tab-label {
    background: #1a252f;
  }
  input:checked + .tab-label::after {
    transform: rotate(90deg);
  }
  input:checked ~ .tab-content {
    max-height: 100vh;
    padding: 1em;
  }
`

const HtmlWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  // max-width: calc(140 * var(--atom));
  // height: 'auto'
  // max-width: calc(140 * var(--atom));
  margin: 0 auto;
  border-radius: 5px;
  border: none;
`;

const FilePreview: React.FC<Props> = ({ file, closeFn, groupTitle }) => {
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [fileData, setFileData] = useState<MediaFile | null>(null);
  const [htmlValue, setHtmlValue] = useState<string>("");

  const { t, i18n } = useTranslation();
  const { setVisibleError, setRetryFn, setErrorMsg } = usePageError();

  //get entity data
  const { refetch: getFileData } = useQuery(
    "getFileData",
    async () => {
      // setVisible(false);
      const data = await axios(`folders/${file?.id}?include.image`, {
        headers: {
          "Accept-Language": i18n.language,
        },
      });
      return data;
    },
    {
      onSuccess: (data: AxiosResponse<any>) => {
        if (!axiosOrg.isAxiosError(data)) {
          setFileData(data?.data?.data);
        }
      },
      onError: (e: AxiosError<any>) => {
        setErrorMsg(e.response?.data?.message || `${t("common.errorGettingData")}`);
        setVisibleError(true);
        setRetryFn({
          execute: getFileData,
        });
      },
    }
  );

  //get html data
  const { refetch: getHtmlData } = useQuery(
    "getHtmlData",
    async () => {
      // setVisible(false);
      const data = await axiosOrg(`${fileData?.path?.path}-download`, {
        headers: {
          "Accept-Language": i18n.language,
        },
      });
      return data;
    },
    {
      onSuccess: (data: AxiosResponse<any>) => {
        if (!axiosOrg.isAxiosError(data)) {
          setHtmlValue(data?.data || "");
        }
      },
      onError: (e: AxiosError<any>) => {
        setErrorMsg(e.response?.data?.message || `${t("common.errorGettingData")}`);
        setVisibleError(true);
        setRetryFn({
          execute: getHtmlData,
        });
      },
    }
  );


  const downloadFile = () => {
    const url = fileData?.path?.path.split("/").pop();
    setLoadingDownload(true);
    axiosOrg({
      url: `${baseURL}` + url + "-download",
      method: "GET",
      responseType: "blob",
    })
      .then((response) => {
        const fileURL = window.URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.setAttribute("download", `${file?.name}`);
        document.body.appendChild(fileLink);
        fileLink.click();
        fileLink.remove();
      })
      .catch((e: AxiosError<any>) => {
        toast.error(e.response?.data?.message || `${t("common.errorDownloadingFile")}`);
      })
      .finally(() => {
        setLoadingDownload(false);
      });
  };

  useEffect(() => {
    getFileData();
  }, []);

  useEffect(() => {
    if (fileData?.path?.path) {
      getHtmlData();
    }
  }, [fileData])

  return file ? (
    <Dialog
      open
      classes={{
        paper: "fullscreen",
      }}
      //   classes={{
      //     paper: xxl2 ? "xxl2" : xxl ? "xxl" : large ? "large" : small ? "small" : "big",
      //   }}
      transitionDuration={0}
    >
      <DialogTitle>
        <span>
          {fileData?.name} {/*fileData?.path?.extension && `(${`.${fileData?.path?.extension}`})`*/}
        </span>
        {/* <span>
          <Trans
            i18nKey="common.filePreviewFile"
            tOptions={{ extension: file.extension, locale: file.locale }}
          >
            <strong style={{ textTransform: "uppercase" }}></strong>
          </Trans>
        </span> */}
        {/* <Button
          variant="outlined"
          color="primary"
          size="small"
          className="small-btn"
          sx={{
            width: "auto",
            ml: "auto",
          }}
          startIcon={
            loadingDownload ? (
              <CircularProgress
                sx={{
                  maxWidth: "calc(2.4 * var(--atom))",
                  maxHeight: "calc(2.4 * var(--atom))",
                }}
              />
            ) : (
              <IconDownload />
            )
          }
          onClick={() => downloadFile()}
        >
          {t("common.download")}
        </Button> */}
        <IconButton
          aria-label="close"
          onClick={closeFn}
          sx={{ marginLeft: "calc(2 * var(--atom))" }}
        >
          <IconClose />
        </IconButton>
      </DialogTitle>
      <DialogContent
        className={
          ["jpg", "jpeg", "png", "gif", "webp"].includes(`${fileData?.path?.extension}`)
            ? "img-wrapper"
            : ""
        }
        sx={
          !["pdf", "html", "jpg", "jpeg", "png", "gif", "webp"].includes(
            `${fileData?.path?.extension}`
          )
            ? {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }
            : {}
        }
      >
        {fileData?.path?.extension === "pdf" && <PdfViewer file={`${fileData?.path?.path}`} />}
        {["jpg", "jpeg", "png", "gif", "webp"].includes(`${fileData?.path?.extension}`) && (
          <img
            alt={`img-${fileData?.path?.extension}`}
            src={fileData?.path?.path}
          />
        )}
        {!["pdf", "html", "jpg", "jpeg", "png", "gif", "webp"].includes(
          `${fileData?.path?.extension}`
        ) && <span>{t("common.fileCantPreview")}</span>}
        {fileData?.path?.extension === "html" && htmlValue && (
          <HtmlWrapper>
            <HtmlInnerContent dangerouslySetInnerHTML={{ __html: htmlValue }}></HtmlInnerContent>
            {/* <Iframe
              title={fileData?.name}
              src={`${fileData?.path?.path}-download`}
            /> */}
          </HtmlWrapper>
        )}
      </DialogContent>
    </Dialog>
  ) : null;
};

export default FilePreview;
